import { Component, Input, OnDestroy } from '@angular/core';

import { Subscription, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AttributeModel } from '../../../../Common/Misc/models/attribute.model';
import { attributeTypesEnum } from '../../../../Common/Misc/enums/attribute-types.enum';
import { topicAttributeTypesEnum } from '../../enums/topic-attribute-types.enum';
import { FolderService } from '../../../Core/services/folder.service';
import { AuditableModel } from '../../../../Common/Misc/models/auditable.model';
import { Folder } from '../../models/folder.model';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { MediaSubscription } from '../../models/media-subscription.model';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { RouteType } from '../../enums/route-type.enum';
import { TopicsService } from '../../../Core/services/topics.service';
import { MentionSearchCriteriaModel } from '../../models/mention-search-criteria.model';
import { MentionSearchModel } from '../../models/mention-search.model';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { TagModel } from '../../../../Common/Tags/models/tag.model';
import { DiscoverListTypeEnum } from '../../../../api/mentions/enum/discover-list-type.enum';
import { TargetingSearchesService } from '../../../../Discover/Targeting/services/targeting-searches.service';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { DistributionService } from '../../../../Common/TargetingShared/services/distribution.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MediaTypeEnum } from '../../../../Common/Basic/enums/media-type.enum';

enum SummaryModeEnum {
    search,
    topic,
    folder,
    subscription
}

@Component({
    selector: 'agpr-monitoring-result-summary',
    templateUrl: './monitoring-result-summary.component.html',
    styleUrls: ['./monitoring-result-summary.component.scss'],
    standalone: false
})
export class MonitoringResultSummaryComponent implements OnDestroy {

    @Input() set criteria(criteria: MentionSearchCriteriaModel) {
        this.searchCriteria = criteria;
        this.setBroadcastMediaTypesString();
        this.setFolderTagValues();
        this.setDiscoverListDisplayString();
    }

    searchCriteria: MentionSearchCriteriaModel = null;
    topic: MentionSearchModel = null;
    folder: Folder = null;
    mediaSubscription: MediaSubscription = null;

    attributes: AttributeModel[] = [];
    attributesPanelHeading: string;
    mediaHandles: string;

    mode: SummaryModeEnum;

    folderIncludes: Folder[] = [];
    folderExcludes: Folder[] = [];
    tagIncludes: TagModel[] = [];
    tagExcludes: TagModel[] = [];
    discoverListDisplayString: string;
    tvMediaTypeString: string;

    private tagList: TagModel[];
    private folderList: Folder[];
    private subscriptions: Subscription[] = [];

    readonly summaryModeEnum = SummaryModeEnum;
    readonly mediaTypes = MediaTypeEnum;

    constructor(
        private topicsService: TopicsService,
        private mediaSubscriptionService: MediaSubscriptionService,
        private monitoringRoutingService: MonitoringRoutingService,
        private tagsService: TagsService,
        private folderService: FolderService,
        private targetingSearchesService: TargetingSearchesService,
        private targetingListService: TargetingListService,
        private distributionService: DistributionService,
        private localizationService: LocalizationService,
    ) {
        this.subscriptions.push(
            this.monitoringRoutingService.getCurrentRoute().pipe(filter(x => !!x)).subscribe(
                route => {
                    switch (route.type) {
                        case RouteType.folder:
                        case RouteType.mentions:
                            this.mode = SummaryModeEnum.folder;
                            break;
                        case RouteType.default:
                        case RouteType.topic:
                            this.mode = SummaryModeEnum.topic;
                            break;
                        case RouteType.search:
                        case RouteType.updateSearch:
                        case RouteType.results:
                            this.mode = SummaryModeEnum.search;
                            break;
                        case RouteType.subscriptionResults:
                            this.mode = SummaryModeEnum.subscription;
                            break;
                    }
                    this.setAttributes();
                }
            ),
            this.topicsService.getFocusedTopic().subscribe(
                topic => {
                    this.topic = topic;
                    this.setAttributes();
                }
            ),
            this.folderService.currentResource$.pipe(filter(x => !!x)).subscribe(
                folder => {
                    this.folder = folder;
                    this.setAttributes();
                }
            ),
            this.mediaSubscriptionService.currentResource$.pipe(filter(x => !!x)).subscribe(
                subscription => {
                    this.mediaSubscription = subscription;
                    this.mediaHandles = this.mediaSubscription.getHandlesName();
                    this.setAttributes();
                }
            ),
            combineLatest([
                this.tagsService.all$,
                this.folderService.all$
            ]).subscribe(([tagList, folderList]) => {
                this.tagList = tagList;
                this.folderList = folderList;
                this.setFolderTagValues();
            }),
        );
    }

    /**
     * Generates and sets the primary attributes of the topic/result
     */
    private setAttributes() {
        let auditableModel: AuditableModel;

        switch (this.mode) {
            case SummaryModeEnum.topic:
                auditableModel = this.topic;
                this.attributesPanelHeading = 'Monitoring.result.summary.topicDetails';
                break;
            case SummaryModeEnum.folder:
                auditableModel = this.folder;
                this.attributesPanelHeading = 'Monitoring.result.summary.folderDetails';
                break;
            case SummaryModeEnum.subscription:
                auditableModel = this.mediaSubscription;
                this.attributesPanelHeading = 'Monitoring.result.summary.subscriptionDetails';
                break;
            case SummaryModeEnum.search:
            default:
                auditableModel = null;
                this.attributesPanelHeading = null;
                break;
        }

        if (!auditableModel) {
            return;
        }

        const createdByAttribute: AttributeModel = {
            id: topicAttributeTypesEnum.createdBy,
            label: 'createdBy',
            value: auditableModel.createdByUserId || undefined,
            type: attributeTypesEnum.user
        };

        const dateCreatedAttribute: AttributeModel = {
            id: topicAttributeTypesEnum.dateCreated,
            label: 'createdDate',
            value: auditableModel.createdDate || undefined,
            type: attributeTypesEnum.date
        };

        const lastModifiedByAttribute: AttributeModel = {
            id: topicAttributeTypesEnum.lastModifiedBy,
            label: 'lastModifiedBy',
            value: auditableModel.modifiedByUserId || undefined,
            type: attributeTypesEnum.user
        };

        const lastModifiedDateAttribute: AttributeModel = {
            id: topicAttributeTypesEnum.lastModifiedDate,
            label: 'lastModifiedDate',
            value: auditableModel.modifiedDate || undefined,
            type: attributeTypesEnum.date
        };

        this.attributes = [
            createdByAttribute,
            dateCreatedAttribute,
            lastModifiedByAttribute,
            lastModifiedDateAttribute
        ].filter(x => !!x.value);

        if (this.mode === SummaryModeEnum.topic && this.topic?.autoSavingConfig?.enabled) {
            const lastAutoSavedDateAttribute: AttributeModel = {
                id: topicAttributeTypesEnum.lastModifiedDate,
                label: 'Monitoring.result.summary.lastAutoSaveDate',
                value: this.topic.autoSavingConfig.lastSaveDate || undefined,
                type: attributeTypesEnum.date
            };
            this.attributes.push(lastAutoSavedDateAttribute);
        }
    }

    /**
     * Converts any folder & tag ids to their corresponding objects
     */
    private setFolderTagValues() {
        if (!this.searchCriteria || !this.tagList || !this.folderList) {
            return;
        }

        this.folderIncludes = this.searchCriteria.query.folder.include
            .map(id => this.folderList.find(folder => folder.id === id))
            .filter(x => !!x);

        this.folderExcludes = this.searchCriteria.query.folder.exclude
            .map(id => this.folderList.find(folder => folder.id === id))
            .filter(x => !!x);

        this.tagIncludes = this.searchCriteria.query.tag.include
            .map(id => this.tagList.find(tag => tag.id === id))
            .filter(x => !!x);

        this.tagExcludes = this.searchCriteria.query.tag.exclude
            .map(id => this.tagList.find(tag => tag.id === id))
            .filter(x => !!x);
    }

    private setDiscoverListDisplayString() {
        if (!this.searchCriteria?.query?.discoverListFilter?.discoverList?.include[0]) {
            return;
        }
        const discoverListFilter = this.searchCriteria.query.discoverListFilter;
        if (discoverListFilter.discoverList?.include[0]) {
            this.discoverListDisplayString = this.localizationService.transform(`Discover.listSource.${discoverListFilter.discoverListType}`) + ': ';
            if (discoverListFilter.discoverListType === DiscoverListTypeEnum.targetingList) {
                this.targetingListService.getById(discoverListFilter.discoverList?.include[0]).subscribe(list => {
                    if (list) {
                        this.discoverListDisplayString +=list.name;
                    }
                });
            } else if (discoverListFilter.discoverListType === DiscoverListTypeEnum.distribution) {
                this.distributionService.getDistributionById(discoverListFilter.discoverList?.include[0]).subscribe(distribution => {
                    if (distribution) {
                        this.discoverListDisplayString += distribution.name;
                    }
                });
            } else if (discoverListFilter.discoverListType === DiscoverListTypeEnum.targetingSearch) {
                this.targetingSearchesService.getById(discoverListFilter.discoverList?.include[0]).subscribe(search => {
                    if (search) {
                        this.discoverListDisplayString += search.name;
                    }
                });
            }
        }

    }

    private setBroadcastMediaTypesString() {
        const tvMediaType = this.searchCriteria?.query?.includedMediaTypes?.find(mediaType => mediaType.id === MediaTypeEnum.TV);
        if (!tvMediaType) {
            this.tvMediaTypeString = '';
            return;
        }

        const tvSubTypes = this.searchCriteria?.query?.includedTvTypes ?? [];
        this.tvMediaTypeString = tvSubTypes.length === 1
            ? this.localizationService.transform(tvMediaType.name) + ' (' + this.localizationService.transform(tvSubTypes[0].name) + ')'
            : this.localizationService.transform(tvMediaType.name);

    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
